/**
 * Copy to Webflow, one section at a time.
 *
 * Section-by-section rather than whole-page because there is no documented size limit on a
 * clipboard payload and pasting a whole page is the reported way to find trouble. Only one
 * thing can be on the clipboard at a time anyway, so the panel walks the author through it.
 */

import { useMemo, useState } from 'react';
import {
  checkInvariants,
  docToSections,
  emitXscp,
  wrapEnvelope,
  type Violation,
} from '@lp/exporters';
import { blockRegistry } from '@lp/blocks';
import { useDocStore } from '../store/docStore.js';
import { copyXscp, isSafari } from '../webflow/clipboard.js';

type SectionState = 'idle' | 'copied' | 'stale';

interface Row {
  key: string;
  label: string;
  bytes: number;
  json: string;
  violations: Violation[];
}

export function ExportPanel(): React.ReactElement {
  const store = useDocStore();
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState<Record<string, SectionState>>({});
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo<Row[]>(() => {
    const doc = store.toDoc();
    try {
      return docToSections(doc, blockRegistry).map((section, i) => {
        const envelope = wrapEnvelope(
          emitXscp([section.node], { seedPrefix: `${section.blockType}#${i}` }),
        );
        const json = JSON.stringify(envelope);
        return {
          key: section.blockId,
          label: section.label,
          bytes: json.length,
          json,
          violations: checkInvariants(envelope, json),
        };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return [];
    }
    // `puck` and `assets` are what actually change the output.
  }, [store.puck, store.meta.geo, store.docType]);

  const anyCopied = Object.values(states).some((s) => s !== 'idle');

  async function copy(row: Row): Promise<void> {
    setError(null);
    if (row.violations.length > 0) return;
    const result = await copyXscp(row.json);
    if (!result.ok) {
      setError(
        result.reason === 'safari'
          ? 'Safari can’t write the clipboard format Webflow needs. Use Chrome, Firefox or Edge.'
          : 'Could not write to the clipboard.',
      );
      return;
    }
    setStates((s) => ({ ...s, [row.key]: 'copied' }));
  }

  if (!open) {
    return (
      <button type="button" className="lp-btn lp-btn--primary" onClick={() => setOpen(true)}>
        Copy to Webflow
      </button>
    );
  }

  return (
    <div className="lp-export">
      <div className="lp-export__head">
        <strong>Copy to Webflow</strong>
        <button type="button" className="lp-btn lp-btn--ghost" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      {isSafari() && (
        <p className="lp-export__warn">
          Safari can’t write the clipboard format Webflow needs. Use Chrome, Firefox or Edge.
        </p>
      )}

      <p className="lp-export__intro">
        Copy one section, paste it into the Webflow Designer, then come back for the next.
        Paste in order.
      </p>

      {error && <p className="lp-export__warn">{error}</p>}

      <ol className="lp-export__list">
        {rows.map((row) => {
          const state = states[row.key] ?? 'idle';
          const blocked = row.violations.length > 0;
          return (
            <li key={row.key} className="lp-export__row">
              <div className="lp-export__meta">
                <span className="lp-export__name">{row.label}</span>
                <span className="lp-export__size">{(row.bytes / 1024).toFixed(1)} KB</span>
                {state === 'copied' && <span className="lp-export__chip">Copied</span>}
                {state === 'stale' && (
                  <span className="lp-export__chip lp-export__chip--stale">Changed since sent</span>
                )}
              </div>

              {blocked ? (
                <ul className="lp-export__errors">
                  {row.violations.map((v) => (
                    <li key={v.id}>{v.message}</li>
                  ))}
                </ul>
              ) : (
                <button
                  type="button"
                  className="lp-btn"
                  disabled={isSafari()}
                  onClick={() => copy(row)}
                >
                  {state === 'idle' ? 'Copy' : 'Copy again'}
                </button>
              )}
            </li>
          );
        })}
      </ol>

      {rows.length === 0 && <p className="lp-export__intro">Nothing to copy yet.</p>}

      {anyCopied && (
        <p className="lp-export__hint">
          First paste: <kbd>⌘V</kbd>. Re-sending a section you’ve already pasted:{' '}
          <kbd>⌘⇧V</kbd> so Webflow reuses the existing classes instead of creating copies.
        </p>
      )}
    </div>
  );
}
